import * as RadixDialog from "@radix-ui/react-dialog";
import { PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, NavLink } from "react-router";

import { useCan } from "@/app/session/permissions";
import { NAV_ENTRIES, NAV_GROUPS, type NavEntry } from "@/app/shell/nav";
import { nextSidebarScrollTop } from "@/app/shell/sidebarScroll";
import { sidebarIconTileClass, sidebarRowClass } from "@/app/shell/sidebarStyles";
import { UserMenu } from "@/app/shell/UserMenu";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { MOD_KEY } from "@/shared/lib/platform";
import { EstateLockup } from "@/shared/ui/brand/EstateLockup";
import { PoweredBy } from "@/shared/ui/brand/PoweredBy";
import { Tooltip } from "@/shared/ui/Tooltip";

export interface SidebarActions {
  onNavigate?: () => void;
  onShowShortcuts: () => void;
  onShowAbout: () => void;
}

/** One main section; hidden from people without its permission. */
function SidebarNavItem({
  entry,
  onNavigate,
  collapsed,
}: {
  entry: NavEntry;
  onNavigate?: (() => void) | undefined;
  collapsed: boolean;
}) {
  const t = useT();
  const allowed = useCan(entry.permission);
  if (!allowed) return null;
  const Icon = entry.icon;
  const label = t(entry.labelId);
  const link = (
    <NavLink
      to={entry.to}
      end={entry.end}
      onClick={onNavigate}
      aria-label={collapsed ? label : undefined}
      className={(state) => cn(sidebarRowClass(state), collapsed && "w-11 justify-center px-0")}
    >
      {({ isActive }) => (
        <>
          <span className={sidebarIconTileClass({ isActive })}>
            <Icon aria-hidden="true" />
          </span>
          {collapsed ? null : <span className="min-w-0 flex-1 truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip label={label} side="end">
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </li>
  );
}

function CollapseButton({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const t = useT();
  const label = t(collapsed ? "shell.expandSidebar" : "shell.collapseSidebar");
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <Tooltip label={`${label} (${MOD_KEY}+B)`} side="bottom">
      <button
        type="button"
        onClick={onToggle}
        aria-label={label}
        aria-expanded={!collapsed}
        className="grid size-8 shrink-0 place-items-center rounded-lg text-rail-ink-subtle transition-colors hover:bg-rail-hover hover:text-rail-ink [&_svg]:size-[17px] rtl:[&_svg]:-scale-x-100"
      >
        <Icon aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

/** Keep wheel movement inside the scrolling nav so the page behind it does not move. */
function useContainedWheel<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      const scroller = node.querySelector<HTMLElement>("[data-sidebar-nav]") ?? node;
      const next = nextSidebarScrollTop(
        scroller.scrollTop,
        scroller.scrollHeight,
        scroller.clientHeight,
        event.deltaY,
        getComputedStyle(scroller).overflowY,
      );
      if (next === null) return;
      event.preventDefault();
      scroller.scrollTop = next;
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
    };
  }, []);
  return ref;
}

interface BodyProps extends SidebarActions {
  collapsed?: boolean;
  onToggle?: () => void;
}

/** The rail's content, shared by the desktop sidebar and the mobile sheet. */
function SidebarBody({ collapsed = false, onToggle, ...actions }: BodyProps) {
  const t = useT();
  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", collapsed && "items-center")}>
      <div
        className={cn(
          "flex h-11 shrink-0 items-center gap-2",
          collapsed ? "flex-col h-auto" : "pe-1",
        )}
      >
        <Link
          to="/"
          onClick={actions.onNavigate}
          aria-label={t("app.name")}
          className="flex min-w-0 flex-1 items-center rounded-lg px-1.5 py-1 outline-none focus-visible:ring-2 focus-visible:ring-rail-accent/60"
        >
          <EstateLockup tone="dark" markOnly={collapsed} />
        </Link>
        {onToggle ? <CollapseButton collapsed={collapsed} onToggle={onToggle} /> : null}
      </div>

      <nav
        data-sidebar-nav=""
        aria-label={t("shell.mainNavigation")}
        className={cn(
          "mt-4 flex min-h-0 flex-1 flex-col overscroll-contain",
          collapsed
            ? "items-center gap-3"
            : "gap-4 overflow-y-auto pe-1 [scrollbar-width:thin] [scrollbar-color:var(--color-rail-raised)_transparent]",
        )}
      >
        {NAV_GROUPS.map((group, index) => (
          <div
            key={group.id}
            className={cn("hidden flex-col has-[li]:flex", collapsed && "items-center")}
          >
            {collapsed ? (
              index > 0 ? (
                <hr className="mb-3 w-6 border-rail-line" />
              ) : null
            ) : group.labelId ? (
              <h2 className="mb-1.5 px-2.5 text-[10.5px] font-semibold tracking-[0.14em] text-rail-ink-subtle uppercase">
                {t(group.labelId)}
              </h2>
            ) : null}
            <ul className="flex flex-col gap-0.5">
              {NAV_ENTRIES.filter((entry) => entry.group === group.id).map((entry) => (
                <SidebarNavItem
                  key={entry.id}
                  entry={entry}
                  onNavigate={actions.onNavigate}
                  collapsed={collapsed}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn("shrink-0 border-t border-rail-line pt-3", collapsed ? "" : "w-full")}>
        <UserMenu {...actions} compact={collapsed} />
        {collapsed ? null : <PoweredBy tone="dark" className="mt-3 px-2" />}
      </div>
    </div>
  );
}

interface SidebarProps extends SidebarActions {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle, ...actions }: SidebarProps) {
  const asideRef = useContainedWheel<HTMLElement>();
  return (
    <aside
      ref={asideRef}
      className={cn(
        "sticky top-0 hidden h-dvh min-h-0 shrink-0 flex-col bg-rail py-4 transition-[width] duration-200 ease-[var(--ease-out-404)] lg:flex",
        collapsed ? "w-[4.75rem] overflow-visible px-2.5" : "w-[16rem] overflow-hidden ps-4 pe-3",
      )}
    >
      <SidebarBody {...actions} collapsed={collapsed} onToggle={onToggle} />
    </aside>
  );
}

interface MobileMenuProps extends SidebarActions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The same rail as a sheet from the start edge, below the desktop breakpoint. */
export function MobileMenu({ open, onOpenChange, ...actions }: MobileMenuProps) {
  const t = useT();
  const close = () => {
    onOpenChange(false);
  };
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-scrim lg:hidden" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 start-0 z-50 flex w-[18rem] max-w-[86vw] animate-sheet-in flex-col bg-rail ps-4 pe-3 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] shadow-overlay [--sheet-from:-24px] lg:hidden rtl:[--sheet-from:24px]"
        >
          <RadixDialog.Title className="sr-only">{t("shell.mainNavigation")}</RadixDialog.Title>
          <RadixDialog.Close
            aria-label={t("common.close")}
            className="absolute end-3 top-[max(1.1rem,calc(env(safe-area-inset-top)+0.35rem))] z-10 grid size-8 place-items-center rounded-lg text-rail-ink-subtle transition-colors hover:bg-rail-hover hover:text-rail-ink"
          >
            <X className="size-4" />
          </RadixDialog.Close>
          <SidebarBody
            {...actions}
            onNavigate={close}
            onShowShortcuts={() => {
              close();
              actions.onShowShortcuts();
            }}
            onShowAbout={() => {
              close();
              actions.onShowAbout();
            }}
          />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
